import React, { useEffect, useRef } from 'react';

/**
 * WebsiteFx — global, additive ambient layer for the whole site.
 *
 *  • Scroll-progress beam (top, scaleX-driven)
 *  • Cursor-tracking glow (desktop only, idles when mouse stops)
 *  • Drifting red orbs (pure CSS keyframes — zero JS per frame)
 *
 * Performance-tuned:
 *  - No SVG gooey filter (was a major GPU/paint cost).
 *  - Orbs animate via CSS only (`fx-orb-drift`) so the JS thread is free.
 *  - Cursor RAF auto-pauses ~600 ms after the pointer stops moving.
 *  - Honors `prefers-reduced-motion` and coarse-pointer devices.
 */
export default function WebsiteFx() {
  const beamRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isCoarse = window.matchMedia('(pointer: coarse)').matches;

    // ---------- Scroll Progress Beam (rAF-throttled, passive) ----------
    let scrollPending = false;
    const onScroll = () => {
      if (scrollPending) return;
      scrollPending = true;
      requestAnimationFrame(() => {
        scrollPending = false;
        const beam = beamRef.current;
        if (!beam) return;
        const doc = document.documentElement;
        const max = Math.max((doc.scrollHeight || 1) - window.innerHeight, 1);
        const pct = Math.min(1, Math.max(0, (window.scrollY || 0) / max));
        beam.style.transform = `scaleX(${pct})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // ---------- Cursor-Tracking Glow (idle-aware) ----------
    // RAF only runs while the pointer is moving; it self-cancels ~600 ms
    // after the last movement so the page is truly idle when nothing changes.
    let cleanupCursor = () => {};
    if (!reduced && !isCoarse) {
      let mx = window.innerWidth / 2;
      let my = window.innerHeight / 2;
      let gx = mx;
      let gy = my;
      let rafId = 0;
      let stopAt = 0;
      const tick = () => {
        gx += (mx - gx) * 0.16;
        gy += (my - gy) * 0.16;
        const g = glowRef.current;
        if (g) g.style.transform = `translate3d(${gx - 280}px, ${gy - 280}px, 0)`;
        if (performance.now() < stopAt || Math.abs(mx - gx) > 0.5 || Math.abs(my - gy) > 0.5) {
          rafId = requestAnimationFrame(tick);
        } else {
          rafId = 0;
        }
      };
      const onMove = (e) => {
        mx = e.clientX;
        my = e.clientY;
        stopAt = performance.now() + 600; // keep animating 600ms past last move
        if (!rafId) rafId = requestAnimationFrame(tick);
      };
      window.addEventListener('pointermove', onMove, { passive: true });
      cleanupCursor = () => {
        window.removeEventListener('pointermove', onMove);
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      cleanupCursor();
    };
  }, []);

  return (
    <>
      {/* Scroll-progress beam */}
      <div
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 right-0 z-[100] h-[2px] origin-left"
        style={{
          transform: 'scaleX(0)',
          transformOrigin: '0 50%',
          willChange: 'transform',
          background:
            'linear-gradient(90deg, rgba(239,43,43,0) 0%, rgba(239,43,43,0.95) 30%, rgba(255,82,82,1) 70%, rgba(239,43,43,0) 100%)',
          boxShadow: '0 0 14px rgba(239,43,43,0.85), 0 0 36px rgba(239,43,43,0.55)',
        }}
        ref={beamRef}
        data-testid="fx-scroll-beam"
      />

      {/* Ambient drifting orbs — pure CSS animation (no JS, no gooey filter) */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[0] overflow-hidden hidden md:block"
      >
        <span className="fx-orb fx-orb-1" />
        <span className="fx-orb fx-orb-2" />
        <span className="fx-orb fx-orb-3" />
      </div>

      {/* Cursor-tracking glow — desktop only */}
      <div
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[1] hidden md:block"
        ref={glowRef}
        style={{
          width: '560px',
          height: '560px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(239,43,43,0.14) 0%, rgba(239,43,43,0.07) 35%, rgba(239,43,43,0) 70%)',
          willChange: 'transform',
          transform: 'translate3d(-9999px, -9999px, 0)',
        }}
      />
    </>
  );
}
